import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { MdWbSunny, MdDarkMode } from 'react-icons/md';

const themeToggleSlideLeftFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ToggleButton = styled(motion.button)`
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--color-text);
  animation: ${themeToggleSlideLeftFadeIn} 0.6s ease-out forwards;
  animation-delay: 0.7s;
  opacity: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const IconContainer = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SunIcon = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MoonIcon = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  const sunVariants = {
    light: { 
      opacity: 0, 
      scale: 0.8,
    },
    dark: { 
      opacity: 1, 
      scale: 1,
    }
  };

  const moonVariants = {
    light: { 
      opacity: 1, 
      scale: 1,
    },
    dark: { 
      opacity: 0, 
      scale: 0.8,
    }
  };

  return (
    <ToggleButton
      onClick={toggleTheme}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      <IconContainer>
        <SunIcon
          variants={sunVariants}
          initial={theme === 'light' ? 'light' : 'dark'}
          animate={theme === 'light' ? 'light' : 'dark'}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <MdWbSunny size={18} />
        </SunIcon>
        <MoonIcon
          variants={moonVariants}
          initial={theme === 'light' ? 'light' : 'dark'}
          animate={theme === 'light' ? 'light' : 'dark'}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <MdDarkMode size={18} />
        </MoonIcon>
      </IconContainer>
    </ToggleButton>
  );
};

export default NavigationThemeToggle;
